import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { StepDots } from "../../components/StepDots";

interface OnboardingLayoutProps {
  /** 1-based step on the **adult** path; omitted on screens outside it. */
  step?: number | undefined;
  totalSteps?: number | undefined;
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
  /** Bottom-anchored primary action. */
  action: ReactNode;
  /** Sits under the action — "Already have an account?" and friends. */
  footer?: ReactNode | undefined;
  onBack?: (() => void) | undefined;
  showBack?: boolean | undefined;
}

/**
 * The shape every onboarding screen shares: back chevron + step dots, headline,
 * input zone, bottom-anchored primary action.
 *
 * Built once and instantiated seven times, as the build pack requires. The
 * action is bottom-anchored rather than following the content because on a
 * 360×800 phone with a keyboard open, a button that flows with content lands
 * under the keyboard exactly when it is needed.
 */
export function OnboardingLayout({
  step,
  totalSteps = 5,
  title,
  subtitle,
  children,
  action,
  footer,
  onBack,
  showBack = true,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();

  return (
    <div className="app-column px-5 pb-6 pt-5">
      <header className="flex items-center justify-between">
        {showBack ? (
          <button
            type="button"
            aria-label="Go back"
            onClick={onBack ?? (() => void navigate(-1))}
            className="-m-3 p-3 text-text"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 5l-7 7 7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <span className="w-6" />
        )}

        {step ? <StepDots current={step} total={totalSteps} /> : <span />}

        {/* Balances the back chevron so the dots stay optically centred. */}
        <span className="w-6" />
      </header>

      <div className="flex flex-col gap-2 pt-6">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[27px] font-bold leading-[1.15] tracking-[-0.01em]">
          {title}
        </h1>
        {subtitle ? (
          <p className="m-0 text-[13.5px] leading-[1.55] text-text-muted">{subtitle}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 pt-6">{children}</div>

      {/* mt-auto is what anchors the action to the bottom on a short screen
          while still scrolling naturally on a long one. */}
      <div className="mt-auto flex flex-col gap-3 pt-8">
        {action}
        {footer}
      </div>
    </div>
  );
}
